import { FileActionButton } from "../file-action/file-action-controls";
import React from "react";
import * as XLSX from "xlsx";

interface RawMaterialMovementLog {
  movementType: string;
  createdDate: string;
  rawMaterialName: string;
  quantity: number;
  vendorName: string;
  invoiceNumber: string;
  sourceType: string;
  sourceValue: string | null;
  documentDetails: string;
  note: string;
}

interface ExportMovementLogProps {
  data: RawMaterialMovementLog[];
  disabled?: boolean;
}

const RawMaterialExportMovementLog: React.FC<ExportMovementLogProps> = ({
  data,
  disabled = false,
}) => {
  const exportToExcel = () => {
    if (!data || data.length === 0) return;

    try {
      const exportData = data.map((item: RawMaterialMovementLog) => ({
        "Movement Type": item.movementType,
        "Date": new Date(item.createdDate).toLocaleDateString("en-GB"),
        "Raw Material Name": item.rawMaterialName,
        "Quantity": item.quantity,
        "Vendor Name": item.vendorName,
        "Invoice Number": item.invoiceNumber,
        "Source Type": item.sourceType,
        "Source / Reference": item.sourceValue || "-",
        "Document Details": item.documentDetails,
        "Note": item.note,
      }));

      // Create worksheet header
      const header = [
        "Movement Type",
        "Date",
        "Raw Material Name",
        "Quantity",
        "Vendor Name",
        "Invoice Number",
        "Source Type",
        "Source / Reference",
        "Document Details",
        "Note",
      ];

      // Create rows
      const rows = exportData.map((r: any) => [
        r["Movement Type"],
        r["Date"],
        r["Raw Material Name"],
        r["Quantity"],
        r["Vendor Name"],
        r["Invoice Number"],
        r["Source Type"],
        r["Source / Reference"],
        r["Document Details"],
        r["Note"],
      ]);

      const worksheet = XLSX.utils.aoa_to_sheet([header, ...rows]);

      // Format Quantity column as number with decimals
      rows.forEach((_: any, i: number) => {
        const rowIndex = i + 1;
        const qtyCell = XLSX.utils.encode_cell({ r: rowIndex, c: 3 });

        if (worksheet[qtyCell]) {
          worksheet[qtyCell].t = "n";
          worksheet[qtyCell].z = "#,##0.00";
        }
      });

      // Set column widths for better readability
      worksheet["!cols"] = [
        { width: 15 }, // Movement Type
        { width: 12 }, // Date
        { width: 25 }, // Raw Material Name
        { width: 12 }, // Quantity
        { width: 20 }, // Vendor Name
        { width: 18 }, // Invoice Number
        { width: 15 }, // Source Type
        { width: 20 }, // Source / Reference
        { width: 25 }, // Document Details
        { width: 20 }, // Note
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Movement Log");

      XLSX.writeFile(workbook, "RawMaterialMovementLog.xlsx");
    } catch (error) {
      console.error("Error exporting movement log to Excel:", error);
    }
  };

  return (
    <FileActionButton action="export"
      label="Export"
      icon="pi pi-file-excel"
      className="p-button-success"
      onClick={exportToExcel}
      disabled={disabled || !data || data.length === 0}
      tooltip={
        !data || data.length === 0
          ? "No data to export"
          : "Export to Excel"
      }
      tooltipOptions={{ position: "top" }}
    />
  );
};

export default RawMaterialExportMovementLog;
